import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

export interface HelpRequest {
  key?: string;
  tab?: string;
}

export interface HelpValue {
  isOpen: boolean;
  request: HelpRequest;
  open: (request?: HelpRequest) => void;
  close: () => void;
  toggle: () => void;
}

const HelpContext = createContext<HelpValue | null>(null);

export function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (target.isContentEditable) return true;
  return target.getAttribute('role') === 'textbox';
}

export function isHelpShortcut(event: KeyboardEvent): boolean {
  return event.key === '?' && !event.ctrlKey && !event.metaKey && !event.altKey && !event.defaultPrevented && !isEditableTarget(event.target);
}

export function HelpProvider({ children, enabled = true }: { children: ReactNode; enabled?: boolean }) {
  const [isOpen, setOpen] = useState(false);
  const [request, setRequest] = useState<HelpRequest>({});
  const { pathname } = useLocation();

  const open = useCallback((next: HelpRequest = {}) => {
    setRequest(next);
    setOpen(true);
  }, []);
  const close = useCallback(() => setOpen(false), []);
  const toggle = useCallback(() => {
    setOpen((current) => {
      if (!current) setRequest({});
      return !current;
    });
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!enabled) return;
    const onKey = (event: KeyboardEvent) => {
      if (!isHelpShortcut(event)) return;
      event.preventDefault();
      toggle();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [enabled, toggle]);

  const value = useMemo<HelpValue>(() => ({ isOpen, request, open, close, toggle }), [isOpen, request, open, close, toggle]);
  return <HelpContext.Provider value={enabled ? value : null}>{children}</HelpContext.Provider>;
}

export function useHelp(): HelpValue | null {
  return useContext(HelpContext);
}
